"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const axios_1 = __importDefault(require("axios"));
const FieldAnalytics_1 = __importDefault(require("../models/FieldAnalytics"));
const Acreage_1 = __importDefault(require("@becrop/components/Modules/Common/Models/Acreage"));
class FieldAnalyticsService {
    constructor() {
        this.USE_MOCK_DATA = false;
        this.totalPages = 1;
        this.totalElements = 1;
    }
    async getFields(farmId, areaId, page) {
        try {
            const response = this.USE_MOCK_DATA
                ? this.generateFakeResponse(page, 10)
                : (await axios_1.default.get(`/api/farms/${farmId}/fields/${areaId}/tabular?page=${page}`)).data;
            this.totalPages = response.meta.last_page;
            this.totalElements = response.meta.total;
            return response.data
                .map((field) => this.mapToFieldAnalytics(field))
                .filter((field) => Boolean(field));
        }
        catch (error) {
            console.error("Error fetching fields:", error);
            throw error;
        }
    }
    mapToFieldAnalytics(field) {
        if (!field)
            return null;
        const fieldData = {
            id: field.id,
            name: field.name,
            extension: field.extension.value,
            geodata: field.geodataSVG,
            crop: { name: "", logo: "", color: "" },
            unit: field.extension.unit,
            recommendations: [],
            acreage: new Acreage_1.default(field.extension.value, field.extension.unit, true)
        };
        const rc = !this.USE_MOCK_DATA ? field.recommendations.map((rc) => {
            return {
                name: rc.name,
                category: rc.category,
                subCategory: rc.subCategory,
                type: rc.type
            };
        }) : field.recommendations;
        const ph = !this.USE_MOCK_DATA ? field.phch.map((phc) => {
            return {
                name: phc.name + '-' + phc.metricCode,
                metricCode: phc.metricCode,
                valueByFarm: Math.round(phc.valueByFarm * 10) / 10,
                unit: phc.unit,
                acreageByFarm: new Acreage_1.default(phc.acreageByFarm.value, phc.acreageByFarm.unit, true)
            };
        }) : field.phch;
        const nd = !this.USE_MOCK_DATA ? field.needs.map((nd) => {
            return {
                name: nd.name,
                metricCode: nd.metricCode,
                ranks: {
                    veryLow: { acreageByFarm: new Acreage_1.default(nd.ranks.veryLow.acreageByFarm.value, nd.ranks.veryLow.acreageByFarm.unit, true) },
                    low: { acreageByFarm: new Acreage_1.default(nd.ranks.low.acreageByFarm.value, nd.ranks.low.acreageByFarm.unit, true) },
                    medium: { acreageByFarm: new Acreage_1.default(nd.ranks.medium.acreageByFarm.value, nd.ranks.medium.acreageByFarm.unit, true) },
                    high: { acreageByFarm: new Acreage_1.default(nd.ranks.high.acreageByFarm.value, nd.ranks.high.acreageByFarm.unit, true) },
                    veryHigh: { acreageByFarm: new Acreage_1.default(nd.ranks.veryHigh.acreageByFarm.value, nd.ranks.veryHigh.acreageByFarm.unit, true) },
                },
                valueByFarm: nd.valueByFarm
            };
        }) : field.needs;
        const validNames = ['Diseases', 'Enfermedades', 'Maladies'];
        const mt = !this.USE_MOCK_DATA ? field.categories.map((ct) => {
            return {
                name: ct.name,
                metrics: ct.metrics.map((mt) => {
                    return {
                        name: validNames.includes(ct.name) ? mt.name + ' (MAX)' : mt.name + ' (W.AVG)',
                        metricCode: mt.metricCode,
                        ranks: {
                            veryLow: { acreageByFarm: new Acreage_1.default(mt.ranks.veryLow.acreageByFarm.value, mt.ranks.veryLow.acreageByFarm.unit, true) },
                            low: { acreageByFarm: new Acreage_1.default(mt.ranks.low.acreageByFarm.value, mt.ranks.low.acreageByFarm.unit, true) },
                            medium: { acreageByFarm: new Acreage_1.default(mt.ranks.medium.acreageByFarm.value, mt.ranks.medium.acreageByFarm.unit, true) },
                            high: { acreageByFarm: new Acreage_1.default(mt.ranks.high.acreageByFarm.value, mt.ranks.high.acreageByFarm.unit, true) },
                            veryHigh: { acreageByFarm: new Acreage_1.default(mt.ranks.veryHigh.acreageByFarm.value, mt.ranks.veryHigh.acreageByFarm.unit, true) },
                        },
                        valueByFarm: mt.valueByFarm
                    };
                })
            };
        }) : field.categories;
        return new FieldAnalytics_1.default(fieldData, rc, nd, mt, field.crops, ph);
    }
    generateFakeResponse(page = 1, perPage = 10) {
        const totalItems = 50;
        const lastPage = Math.ceil(totalItems / perPage);
        const from = (page - 1) * perPage + 1;
        const to = Math.min(page * perPage, totalItems);
        const data = Array.from({ length: to - from + 1 }, (_, i) => this.generateMockField(from + i));
        const baseUrl = "https://example.com/api/fields";
        return {
            links: {
                first: `${baseUrl}?page=1`,
                last: `${baseUrl}?page=${lastPage}`,
                prev: page > 1 ? `${baseUrl}?page=${page - 1}` : null,
                next: page < lastPage ? `${baseUrl}?page=${page + 1}` : null,
            },
            meta: {
                current_page: page,
                from,
                last_page: lastPage,
                links: Array.from({ length: lastPage }, (_, i) => ({
                    url: `${baseUrl}?page=${i + 1}`,
                    label: `${i + 1}`,
                    active: page === i + 1,
                })),
                path: baseUrl,
                per_page: perPage,
                to,
                total: totalItems,
            },
            data,
        };
    }
    generateMockField(id) {
        const randomValue = (min, max, decimals = 1) => +((Math.random() * (max - min)) + min).toFixed(decimals);
        const randomFromArray = (arr) => arr[Math.floor(Math.random() * arr.length)];
        const generateArea = () => (new Acreage_1.default(randomValue(10, 500), randomFromArray(["hectares", "acres"])));
        const cropOptions = [
            { id: 1, name: "Banana", logo: "banana", color: "#d8af04" },
            { id: 2, name: "Corn", logo: "corn", color: "#f7a800" },
            { id: 3, name: "Lemon", logo: "lemon", color: "#e8d33d" },
            { id: 4, name: "Tuber", logo: "tuber", color: "#9d7111" },
            { id: 5, name: "Agave", logo: "agave", color: "#42683e" },
            { id: 6, name: "Grape", logo: "grape", color: "#743781" },
            { id: 7, name: "Strawberry", logo: "strawberry", color: "#ba1031" },
            { id: 8, name: "Nut", logo: "nut", color: "#b9540f" }
        ];
        const recommendationNames = ["Trichoderma", "Mycorrhizae", "Biochar"];
        const recommendationCategories = ["nutrition", "diseases", "sustainability"];
        const subCategories = ["Biostimulant", "Fertilizer", "Organic"];
        const metricNames = ["Phosphorus Mobilization", "Nitrogen Fixation"];
        const metricCodes = ["PMO", "NFX"];
        const generateRanks = () => ({
            veryLow: { acreageByFarm: generateArea() },
            low: { acreageByFarm: generateArea() },
            medium: { acreageByFarm: generateArea() },
            high: { acreageByFarm: generateArea() },
            veryHigh: { acreageByFarm: generateArea() },
        });
        function generateRandomPhchData() {
            const metricDefinitions = [
                { name: "Iron", metricCode: "Fe (H3A)", unit: "mg/kg", min: 10, max: 500 },
                { name: "Iron", metricCode: "Fe (H3A)", unit: "ppm", min: 10, max: 500 },
                { name: "Zinc", metricCode: "Zn (H3A)", unit: "ppm", min: 1, max: 50 },
                { name: "Copper", metricCode: "Cu (H3A)", unit: "ppm", min: 0.5, max: 25 },
                { name: "Manganese", metricCode: "Mn (H3A)", unit: "ppm", min: 5, max: 200 },
                { name: "Aluminum", metricCode: "Al (H3A)", unit: "mg/kg", min: 50, max: 1000 },
                { name: "Boron", metricCode: "B (H3A)", unit: "ppm", min: 0.1, max: 5 },
                { name: "Phosphorus", metricCode: "P (H3A)", unit: "mg/kg", min: 5, max: 100 },
            ];
            return metricDefinitions.map(metric => ({
                name: metric.name,
                metricCode: metric.metricCode,
                valueByFarm: randomValue(metric.min, metric.max),
                unit: metric.unit,
                acreageByFarm: new Acreage_1.default(randomValue(10, 500), randomFromArray(["hectares", "acres"])),
            }));
        }
        return {
            id,
            name: `Field COL ${id} Cafexport MujAntioquia Original *AG, Toledo, Madridejos - SurEste`,
            geodataSVG: "M -121.500000 -38.100000 L -121.500000 -38.090000 -121.490000 -38.090000 -121.490000 -38.100000 Z",
            crops: [randomFromArray(cropOptions)],
            extension: generateArea(),
            recommendations: [
                {
                    name: randomFromArray(recommendationNames),
                    category: randomFromArray(recommendationCategories),
                    subCategory: randomFromArray(subCategories),
                    type: [
                        {
                            code: "NUT",
                            value: 132.3,
                            unit: "hectares"
                        },
                        {
                            code: "SHI",
                            value: 312.3,
                            unit: "hectares"
                        }
                    ]
                },
            ],
            needs: [
                {
                    name: "Health",
                    metricCode: "HLT",
                    ranks: generateRanks(),
                    valueByFarm: randomValue(0, 100)
                },
            ],
            phch: generateRandomPhchData(),
            categories: [
                {
                    name: "Nitrogen pathways (N)",
                    metrics: [
                        {
                            name: randomFromArray(metricNames),
                            metricCode: randomFromArray(metricCodes),
                            ranks: generateRanks(),
                            valueByFarm: randomValue(0, 100)
                        },
                    ],
                },
            ],
        };
    }
}
exports.default = FieldAnalyticsService;
